import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

export type PlotSelectionTab = "seeds" | "biologicals" | "nutrient";
export type PlotSelectionTargetMode = "plot" | "stripe";

export interface PlotSelectionScope {
  domain: PlotSelectionTab;
  applicationId?: string | number;
  targetMode: PlotSelectionTargetMode;
  trialId?: string | number;
}

type PlotSelectionScopeInput = Omit<PlotSelectionScope, "targetMode"> & {
  targetMode?: PlotSelectionTargetMode;
};

export interface AssignedSeedPlot {
  cellId: string;
  ownerId: string;
  ownerLabel?: string;
  color: string;
  seedRate?: number | null;
}

export interface AssignedSeedStripe {
  cellId: string;
  ownerId: string;
  ownerLabel?: string;
  color: string;
  isStandard?: boolean;
}

export interface AssignedTreatmentPlot {
  cellId: string;
  ownerId: string;
  ownerLabel?: string;
  applicationId: string | number;
  color: string;
  targetMode?: PlotSelectionTargetMode;
}

export interface PlotSelectionConflict {
  tab: PlotSelectionTab;
  applicationId?: string | number;
  targetId: string;
  ownerId: string;
  ownerLabel?: string;
}

export interface PlotSelectionSession {
  tab: PlotSelectionTab;
  ownerId: string;
  applicationId?: string | number;
  targetMode: PlotSelectionTargetMode;
  targetIds: string[];
  selectedIds: string[];
  disabledIds: string[];
  reassignedIds: string[];
  color: string;
}

interface PlotSelectionContextValue {
  assignedSeedPlots: AssignedSeedPlot[];
  setAssignedSeedPlots: (plots: AssignedSeedPlot[]) => void;
  autoAssignmentDraftPlots: AssignedSeedPlot[];
  setAutoAssignmentDraftPlots: (plots: AssignedSeedPlot[]) => void;
  assignedSeedStripes: AssignedSeedStripe[];
  setAssignedSeedStripes: (stripes: AssignedSeedStripe[]) => void;
  assignedTreatmentPlots: AssignedTreatmentPlot[];
  setAssignedTreatmentPlots: (plots: AssignedTreatmentPlot[]) => void;
  activeScope: PlotSelectionScope | null;
  setActiveScope: (scope: PlotSelectionScopeInput | null) => void;
  session: PlotSelectionSession | null;
  pendingConflict: PlotSelectionConflict | null;
  focusedId: string | null;
  seedSelectionRates: Record<string, number | null>;
  setSeedSelectionRates: (rates: Record<string, number | null>) => void;
  isOutlined: (id: string) => boolean;
  toggleOutline: (id: string) => void;
  startSession: (
    session: Omit<
      PlotSelectionSession,
      "selectedIds" | "targetMode" | "reassignedIds"
    > & {
      selectedIds?: string[];
      targetMode?: PlotSelectionTargetMode;
    }
  ) => void;
  toggleTarget: (id: string) => void;
  setSelectedIds: (ids: string[]) => void;
  openConflict: (conflict: PlotSelectionConflict) => void;
  resolveConflict: (reassign: boolean) => void;
  toggleFocus: (id: string) => void;
  endSession: (expectedOwner?: PlotSelectionSessionIdentity) => void;
}

interface PlotSelectionSessionIdentity {
  tab: PlotSelectionTab;
  ownerId: string;
  applicationId?: string | number;
  targetMode?: PlotSelectionTargetMode;
}

const PlotSelectionContext = createContext<PlotSelectionContextValue | null>(
  null
);

interface PlotSelectionProviderProps {
  children: ReactNode;
}

const uniqueIds = (ids: string[]): string[] => [...new Set(ids)];

export const PlotSelectionProvider = ({
  children,
}: PlotSelectionProviderProps): JSX.Element => {
  const [activeScope, updateActiveScope] = useState<PlotSelectionScope | null>(
    null
  );
  const activeScopeRef = useRef(activeScope);
  activeScopeRef.current = activeScope;
  const [session, setSession] = useState<PlotSelectionSession | null>(null);
  const [pendingConflict, setPendingConflict] =
    useState<PlotSelectionConflict | null>(null);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [seedSelectionRates, updateSeedSelectionRates] = useState<
    Record<string, number | null>
  >({});
  const setSeedSelectionRates = useCallback(
    (rates: Record<string, number | null>): void => {
      updateSeedSelectionRates((previous) =>
        JSON.stringify(previous) === JSON.stringify(rates) ? previous : rates
      );
    },
    []
  );
  const [outlinedIds, setOutlinedIds] = useState<string[]>([]);
  const isOutlined = useCallback(
    (id: string): boolean => outlinedIds.includes(id),
    [outlinedIds]
  );
  const toggleOutline = useCallback((id: string): void => {
    setOutlinedIds((previous) =>
      previous.includes(id)
        ? previous.filter((item) => item !== id)
        : [...previous, id]
    );
  }, []);
  const [assignedSeedPlots, updateAssignedSeedPlots] = useState<
    AssignedSeedPlot[]
  >([]);
  const [autoAssignmentDraftPlots, updateAutoAssignmentDraftPlots] = useState<
    AssignedSeedPlot[]
  >([]);
  const [assignedSeedStripes, updateAssignedSeedStripes] = useState<
    AssignedSeedStripe[]
  >([]);
  const [assignedTreatmentPlots, updateAssignedTreatmentPlots] = useState<
    AssignedTreatmentPlot[]
  >([]);
  const setAssignedSeedPlots = useCallback(
    (plots: AssignedSeedPlot[]): void => {
      updateAssignedSeedPlots((previous) =>
        JSON.stringify(previous) === JSON.stringify(plots) ? previous : plots
      );
    },
    []
  );
  const setAutoAssignmentDraftPlots = useCallback(
    (plots: AssignedSeedPlot[]): void => {
      updateAutoAssignmentDraftPlots((previous) =>
        JSON.stringify(previous) === JSON.stringify(plots) ? previous : plots
      );
    },
    []
  );
  const setAssignedSeedStripes = useCallback(
    (stripes: AssignedSeedStripe[]): void => {
      updateAssignedSeedStripes((previous) =>
        JSON.stringify(previous) === JSON.stringify(stripes)
          ? previous
          : stripes
      );
    },
    []
  );
  const setAssignedTreatmentPlots = useCallback(
    (plots: AssignedTreatmentPlot[]): void => {
      updateAssignedTreatmentPlots((previous) =>
        JSON.stringify(previous) === JSON.stringify(plots) ? previous : plots
      );
    },
    []
  );
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const pendingConflictRef = useRef(pendingConflict);
  pendingConflictRef.current = pendingConflict;

  const clearPendingConflict = useCallback((): void => {
    pendingConflictRef.current = null;
    setPendingConflict(null);
  }, []);

  const setActiveScope = useCallback(
    (scope: PlotSelectionScopeInput | null): void => {
      const nextScope = scope
        ? { ...scope, targetMode: scope.targetMode ?? "plot" }
        : null;
      const previousScope = activeScopeRef.current;
      const scopeChanged =
        previousScope?.domain !== nextScope?.domain ||
        previousScope?.applicationId !== nextScope?.applicationId ||
        previousScope?.targetMode !== nextScope?.targetMode ||
        previousScope?.trialId !== nextScope?.trialId;
      activeScopeRef.current = nextScope;
      updateActiveScope(nextScope);

      const current = sessionRef.current;
      if (
        current &&
        (!nextScope ||
          current.tab !== nextScope.domain ||
          current.applicationId !== nextScope.applicationId ||
          current.targetMode !== nextScope.targetMode ||
          previousScope?.trialId !== nextScope.trialId)
      ) {
        sessionRef.current = null;
        setSession(null);
      }
      if (scopeChanged) {
        setFocusedId(null);
        setSeedSelectionRates({});
        setOutlinedIds([]);
        clearPendingConflict();
      }
    },
    [clearPendingConflict, setSeedSelectionRates]
  );

  const startSession = useCallback<PlotSelectionContextValue["startSession"]>(
    (nextSession) => {
      const targetIds = uniqueIds(nextSession.targetIds);
      const targetIdSet = new Set(targetIds);
      const disabledIds = uniqueIds(nextSession.disabledIds).filter((id) =>
        targetIdSet.has(id)
      );
      const disabledIdSet = new Set(disabledIds);
      const selectedIds = uniqueIds(nextSession.selectedIds ?? []).filter(
        (id) => targetIdSet.has(id) && !disabledIdSet.has(id)
      );

      const next = {
        ...nextSession,
        targetMode: nextSession.targetMode ?? "plot",
        targetIds,
        disabledIds,
        selectedIds,
        reassignedIds: [],
      };
      sessionRef.current = next;
      setSession(next);
      setFocusedId(null);
      setSeedSelectionRates({});
      clearPendingConflict();
    },
    [clearPendingConflict, setSeedSelectionRates]
  );

  const toggleTarget = useCallback((id: string): void => {
    const current = sessionRef.current;
    if (
      pendingConflictRef.current !== null ||
      !current?.targetIds.includes(id) ||
      current.disabledIds.includes(id)
    ) {
      return;
    }

    const selected = current.selectedIds.includes(id);
    const next = {
      ...current,
      selectedIds: selected
        ? current.selectedIds.filter((selectedId) => selectedId !== id)
        : [...current.selectedIds, id],
      reassignedIds: selected
        ? current.reassignedIds.filter((reassignedId) => reassignedId !== id)
        : current.reassignedIds,
    };
    sessionRef.current = next;
    setSession(next);
    setFocusedId((current) => (current === id ? null : current));
  }, []);

  const setSelectedIds = useCallback(
    (ids: string[]): void => {
      const current = sessionRef.current;
      if (!current) {
        return;
      }

      const targetIdSet = new Set(current.targetIds);
      const disabledIdSet = new Set(current.disabledIds);
      const selectedIds = uniqueIds(ids).filter(
        (id) => targetIdSet.has(id) && !disabledIdSet.has(id)
      );
      const selectedIdSet = new Set(selectedIds);
      const next = {
        ...current,
        selectedIds,
        reassignedIds: current.reassignedIds.filter((id) =>
          selectedIdSet.has(id)
        ),
      };
      sessionRef.current = next;
      setSession(next);
      setFocusedId(null);
      setSeedSelectionRates({});
      clearPendingConflict();
    },
    [clearPendingConflict, setSeedSelectionRates]
  );

  const openConflict = useCallback((conflict: PlotSelectionConflict): void => {
    const current = sessionRef.current;
    if (
      pendingConflictRef.current !== null ||
      !current ||
      current.tab !== conflict.tab ||
      current.applicationId !== conflict.applicationId ||
      !current.targetIds.includes(conflict.targetId) ||
      current.disabledIds.includes(conflict.targetId) ||
      current.ownerId === conflict.ownerId
    ) {
      return;
    }

    pendingConflictRef.current = conflict;
    setPendingConflict(conflict);
  }, []);

  const resolveConflict = useCallback(
    (reassign: boolean): void => {
      const conflict = pendingConflictRef.current;
      const current = sessionRef.current;
      clearPendingConflict();

      if (
        !reassign ||
        !conflict ||
        !current ||
        current.tab !== conflict.tab ||
        current.applicationId !== conflict.applicationId ||
        !current.targetIds.includes(conflict.targetId) ||
        current.disabledIds.includes(conflict.targetId)
      ) {
        return;
      }

      const next = {
        ...current,
        selectedIds: current.selectedIds.includes(conflict.targetId)
          ? current.selectedIds
          : [...current.selectedIds, conflict.targetId],
        reassignedIds: current.reassignedIds.includes(conflict.targetId)
          ? current.reassignedIds
          : [...current.reassignedIds, conflict.targetId],
      };
      sessionRef.current = next;
      setSession(next);
    },
    [clearPendingConflict]
  );

  const toggleFocus = useCallback((id: string): void => {
    setFocusedId((current) => (current === id ? null : id));
  }, []);

  const endSession = useCallback(
    (expectedOwner?: PlotSelectionSessionIdentity): void => {
      const current = sessionRef.current;
      if (
        expectedOwner &&
        (current?.tab !== expectedOwner.tab ||
          current.ownerId !== expectedOwner.ownerId ||
          (expectedOwner.applicationId !== undefined &&
            current.applicationId !== expectedOwner.applicationId) ||
          (expectedOwner.targetMode !== undefined &&
            current.targetMode !== expectedOwner.targetMode))
      ) {
        return;
      }

      sessionRef.current = null;
      setSession(null);
      setFocusedId(null);
      setSeedSelectionRates({});
      clearPendingConflict();
    },
    [clearPendingConflict, setSeedSelectionRates]
  );

  const value = useMemo<PlotSelectionContextValue>(
    () => ({
      assignedSeedPlots,
      setAssignedSeedPlots,
      autoAssignmentDraftPlots,
      setAutoAssignmentDraftPlots,
      assignedSeedStripes,
      setAssignedSeedStripes,
      assignedTreatmentPlots,
      setAssignedTreatmentPlots,
      activeScope,
      setActiveScope,
      session,
      pendingConflict,
      focusedId,
      seedSelectionRates,
      setSeedSelectionRates,
      isOutlined,
      toggleOutline,
      startSession,
      toggleTarget,
      setSelectedIds,
      openConflict,
      resolveConflict,
      toggleFocus,
      endSession,
    }),
    [
      assignedSeedPlots,
      setAssignedSeedPlots,
      autoAssignmentDraftPlots,
      setAutoAssignmentDraftPlots,
      assignedSeedStripes,
      setAssignedSeedStripes,
      assignedTreatmentPlots,
      setAssignedTreatmentPlots,
      activeScope,
      setActiveScope,
      session,
      pendingConflict,
      focusedId,
      seedSelectionRates,
      setSeedSelectionRates,
      isOutlined,
      toggleOutline,
      startSession,
      toggleTarget,
      setSelectedIds,
      openConflict,
      resolveConflict,
      toggleFocus,
      endSession,
    ]
  );

  return (
    <PlotSelectionContext.Provider value={value}>
      {children}
    </PlotSelectionContext.Provider>
  );
};

export const usePlotSelection = (): PlotSelectionContextValue => {
  const context = useContext(PlotSelectionContext);

  if (!context) {
    throw new Error(
      "Plot selection requires PlotSelectionProvider to be rendered above it."
    );
  }

  return context;
};

export const useOptionalPlotSelection = (): PlotSelectionContextValue | null =>
  useContext(PlotSelectionContext);
